import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import {
  Alert,
  Badge,
  Button,
  Col,
  Form,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Progress,
  Row,
  Spinner,
} from "reactstrap";

import { canManageClinicalRecords, canViewClinicalRecords } from "../../helpers/clinicalRecordAccess";
import {
  Appointment,
  getAll as listAppointments,
} from "../../services/appointmentService";
import {
  ClinicalProtocol,
  ClinicalProtocolSession,
  createClientClinicalProtocol,
  listClientClinicalProtocols,
  updateClinicalProtocol,
  updateClinicalProtocolSession,
} from "../../services/clinicalProtocolService";
import { getClientCredits } from "../../services/servicePackageService";
import { ClientCredit } from "../../types/servicePackage";
import { getServices, Service } from "../../services/serviceService";
import ClinicalProtocolChecklistPanel from "./ClinicalProtocolChecklistPanel";
import ClinicalProtocolMapPanel from "./ClinicalProtocolMapPanel";

type Props = {
  clientId: number;
};

type MarkTarget = {
  protocol: ClinicalProtocol;
  session: ClinicalProtocolSession;
};

const statusColor = (status: string): string => {
  switch (status) {
    case "active":
    case "done":
      return "success";
    case "completed":
      return "primary";
    case "cancelled":
    case "skipped":
      return "secondary";
    default:
      return "warning";
  }
};

const progressPercent = (protocol: ClinicalProtocol): number => {
  const total = Math.max(protocol.total_sessions, 1);
  const done = Math.min(protocol.sessions_completed_count ?? 0, total);
  return Math.round((done / total) * 100);
};

const ClientClinicalProtocolsPanel: React.FC<Props> = ({ clientId }) => {
  const { t, i18n } = useTranslation();
  const canView = canViewClinicalRecords();
  const canManage = canManageClinicalRecords();

  const [protocols, setProtocols] = useState<ClinicalProtocol[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [name, setName] = useState("");
  const [totalSessions, setTotalSessions] = useState("10");
  const [notes, setNotes] = useState("");
  const [serviceId, setServiceId] = useState("");
  const [clientCreditId, setClientCreditId] = useState("");
  const [services, setServices] = useState<Service[]>([]);
  const [credits, setCredits] = useState<ClientCredit[]>([]);
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [busySessionId, setBusySessionId] = useState<number | null>(null);
  const [markTarget, setMarkTarget] = useState<MarkTarget | null>(null);
  const [appointmentId, setAppointmentId] = useState("");
  const [consumeCredit, setConsumeCredit] = useState(true);
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [loadingAppointments, setLoadingAppointments] = useState(false);
  const [scheduleHint, setScheduleHint] = useState<{
    protocol: ClinicalProtocol;
    kind: "next" | "completed";
  } | null>(null);

  const formatDate = useCallback(
    (value?: string | null) => {
      if (!value) {
        return t("clients.common.notAvailable");
      }
      return new Intl.DateTimeFormat(i18n.language, {
        dateStyle: "short",
        timeStyle: "short",
      }).format(new Date(value));
    },
    [i18n.language, t]
  );

  const load = useCallback(async () => {
    if (!canView || !clientId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const response = await listClientClinicalProtocols(clientId, { per_page: 50 });
      setProtocols(response.data ?? []);
    } catch {
      toast.error(t("clients.protocol.loadError"));
      setProtocols([]);
    } finally {
      setLoading(false);
    }
  }, [canView, clientId, t]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!showForm || !canManage) {
      return;
    }
    getServices({ per_page: 100 })
      .then((response) => setServices(response.data ?? []))
      .catch(() => setServices([]));
    getClientCredits(clientId, { per_page: 100 })
      .then((response) => setCredits(response.data ?? []))
      .catch(() => setCredits([]));
  }, [showForm, canManage, clientId]);

  useEffect(() => {
    if (!markTarget) {
      return;
    }
    const preview = markTarget.protocol.credit_preview;
    setConsumeCredit(Boolean(preview?.can_consume));
    let cancelled = false;
    setLoadingAppointments(true);
    listAppointments({ client_id: clientId, per_page: 30 })
      .then((response) => {
        if (!cancelled) {
          setAppointments(response.data ?? []);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setAppointments([]);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoadingAppointments(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [markTarget, clientId]);

  const replaceProtocol = (next: ClinicalProtocol) => {
    setProtocols((prev) => prev.map((item) => (item.id === next.id ? next : item)));
  };

  const applyAfterMark = (protocol: ClinicalProtocol) => {
    replaceProtocol(protocol);
    if (protocol.status === "completed") {
      setScheduleHint({ protocol, kind: "completed" });
      return;
    }
    if (protocol.status === "active") {
      setScheduleHint({ protocol, kind: "next" });
    }
  };

  const handleCreate = async (event: React.FormEvent) => {
    event.preventDefault();
    const total = Number(totalSessions);
    if (!name.trim() || !Number.isFinite(total) || total < 1) {
      toast.error(t("clients.protocol.validation.required"));
      return;
    }
    setSaving(true);
    try {
      const response = await createClientClinicalProtocol(clientId, {
        name: name.trim(),
        total_sessions: total,
        notes: notes.trim() || null,
        service_id: serviceId ? Number(serviceId) : null,
        client_credit_id: clientCreditId ? Number(clientCreditId) : null,
      });
      toast.success(response.message || t("clients.protocol.createSuccess"));
      setShowForm(false);
      setName("");
      setTotalSessions("10");
      setNotes("");
      setServiceId("");
      setClientCreditId("");
      setExpandedId(response.data.id);
      await load();
    } catch (error: unknown) {
      const apiError = error as { data?: string };
      toast.error(apiError.data || t("clients.protocol.createError"));
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = async (protocol: ClinicalProtocol) => {
    if (!window.confirm(t("clients.protocol.cancelConfirm"))) {
      return;
    }
    setSaving(true);
    try {
      const response = await updateClinicalProtocol(protocol.id, { status: "cancelled" });
      toast.success(response.message || t("clients.protocol.cancelSuccess"));
      replaceProtocol(response.data);
      setScheduleHint(null);
    } catch (error: unknown) {
      const apiError = error as { data?: string };
      toast.error(apiError.data || t("clients.protocol.cancelError"));
    } finally {
      setSaving(false);
    }
  };

  const handleSessionStatus = async (
    protocol: ClinicalProtocol,
    session: ClinicalProtocolSession,
    status: "done" | "skipped" | "planned",
    linkedAppointmentId?: number | null,
    shouldConsumeCredit?: boolean
  ) => {
    setBusySessionId(session.id);
    try {
      const response = await updateClinicalProtocolSession(protocol.id, session.id, {
        status,
        ...(linkedAppointmentId !== undefined
          ? { appointment_id: linkedAppointmentId }
          : {}),
        ...(status === "done" && shouldConsumeCredit !== undefined
          ? { consume_credit: shouldConsumeCredit }
          : {}),
      });
      toast.success(response.message || t("clients.protocol.sessionSuccess"));
      if (status === "done" || status === "skipped") {
        applyAfterMark(response.data.protocol);
      } else {
        replaceProtocol(response.data.protocol);
        setScheduleHint(null);
      }
    } catch (error: unknown) {
      const apiError = error as { data?: string };
      toast.error(apiError.data || t("clients.protocol.sessionError"));
    } finally {
      setBusySessionId(null);
    }
  };

  const confirmMarkDone = async () => {
    if (!markTarget) {
      return;
    }
    const linked = appointmentId ? Number(appointmentId) : null;
    await handleSessionStatus(
      markTarget.protocol,
      markTarget.session,
      "done",
      linked && Number.isFinite(linked) ? linked : null,
      consumeCredit
    );
    setMarkTarget(null);
    setAppointmentId("");
    setConsumeCredit(true);
  };

  const creditOptionLabel = (credit: ClientCredit): string => {
    const packageName = credit.service_package?.name;
    const serviceName = credit.service?.name ?? "";
    const base = packageName || serviceName || t("clients.protocol.credit.unnamed");
    return t("clients.protocol.credit.option", {
      name: base,
      remaining: credit.sessions_remaining,
    });
  };

  if (!canView) {
    return (
      <Alert color="secondary" className="mb-0">
        {t("clients.protocol.noPermission")}
      </Alert>
    );
  }

  if (loading) {
    return (
      <div className="text-center py-4">
        <Spinner color="primary">{t("clients.loading")}</Spinner>
      </div>
    );
  }

  return (
    <div>
      <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
        <p className="text-muted mb-0 small">{t("clients.protocol.hint")}</p>
        {canManage ? (
          <Button
            color="primary"
            size="sm"
            outline={!showForm}
            onClick={() => setShowForm((open) => !open)}
          >
            {showForm
              ? t("clients.protocol.cancelForm")
              : t("clients.protocol.new")}
          </Button>
        ) : null}
      </div>

      {scheduleHint ? (
        <Alert
          color={scheduleHint.kind === "completed" ? "success" : "info"}
          className="d-flex flex-wrap justify-content-between align-items-center gap-2"
          toggle={() => setScheduleHint(null)}
        >
          <span>
            {scheduleHint.kind === "completed"
              ? t("clients.protocol.schedule.completed", {
                  name: scheduleHint.protocol.name,
                })
              : t("clients.protocol.schedule.nextHint", {
                  n: scheduleHint.protocol.current_session_number,
                  m: scheduleHint.protocol.total_sessions,
                  name: scheduleHint.protocol.name,
                })}
          </span>
          {scheduleHint.kind === "next" ? (
            <Button
              color="primary"
              size="sm"
              tag={Link}
              to={`/agenda?client_id=${clientId}`}
            >
              {t("clients.protocol.schedule.nextCta")}
            </Button>
          ) : null}
        </Alert>
      ) : null}

      {showForm && canManage ? (
        <Form onSubmit={handleCreate} className="border rounded p-3 mb-3 bg-light">
          <Row className="g-2">
            <Col md={6}>
              <FormGroup className="mb-2">
                <Label for="protocol-name">{t("clients.protocol.form.name")}</Label>
                <Input
                  id="protocol-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder={t("clients.protocol.form.namePlaceholder")}
                  maxLength={160}
                  required
                />
              </FormGroup>
            </Col>
            <Col md={3}>
              <FormGroup className="mb-2">
                <Label for="protocol-sessions">
                  {t("clients.protocol.form.totalSessions")}
                </Label>
                <Input
                  id="protocol-sessions"
                  type="number"
                  min={1}
                  max={60}
                  value={totalSessions}
                  onChange={(e) => setTotalSessions(e.target.value)}
                  required
                />
              </FormGroup>
            </Col>
            <Col md={3}>
              <FormGroup className="mb-2">
                <Label for="protocol-service">
                  {t("clients.protocol.form.serviceOptional")}
                </Label>
                <Input
                  id="protocol-service"
                  type="select"
                  value={serviceId}
                  onChange={(e) => {
                    setServiceId(e.target.value);
                    setClientCreditId("");
                  }}
                >
                  <option value="">{t("clients.protocol.form.noService")}</option>
                  {services.map((service) => (
                    <option key={service.id} value={service.id}>
                      {service.name}
                    </option>
                  ))}
                </Input>
              </FormGroup>
            </Col>
            <Col xs={12}>
              <FormGroup className="mb-2">
                <Label for="protocol-credit">
                  {t("clients.protocol.form.creditOptional")}
                </Label>
                <Input
                  id="protocol-credit"
                  type="select"
                  value={clientCreditId}
                  onChange={(e) => {
                    const next = e.target.value;
                    setClientCreditId(next);
                    if (next) {
                      const selected = credits.find((item) => item.id === Number(next));
                      if (selected) {
                        setServiceId(String(selected.service_id));
                      }
                    }
                  }}
                >
                  <option value="">{t("clients.protocol.form.noCredit")}</option>
                  {credits
                    .filter(
                      (credit) =>
                        !serviceId || Number(serviceId) === Number(credit.service_id)
                    )
                    .map((credit) => (
                      <option key={credit.id} value={credit.id}>
                        {creditOptionLabel(credit)}
                      </option>
                    ))}
                </Input>
                <p className="text-muted small mb-0 mt-1">
                  {credits.length === 0
                    ? t("clients.protocol.credit.empty")
                    : t("clients.protocol.form.creditHint")}
                </p>
              </FormGroup>
            </Col>
            <Col xs={12}>
              <FormGroup className="mb-2">
                <Label for="protocol-notes">{t("clients.protocol.form.notes")}</Label>
                <Input
                  id="protocol-notes"
                  type="textarea"
                  rows={2}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  maxLength={8000}
                  placeholder={t("clients.protocol.form.notesPlaceholder")}
                />
              </FormGroup>
            </Col>
            <Col xs={12}>
              <Button color="primary" type="submit" disabled={saving}>
                {saving ? t("clients.protocol.saving") : t("clients.protocol.create")}
              </Button>
            </Col>
          </Row>
        </Form>
      ) : null}

      {protocols.length === 0 ? (
        <Alert color="info" className="mb-0">
          <p className="mb-1 fw-medium">{t("clients.protocol.emptyTitle")}</p>
          <p className="mb-0 small">{t("clients.protocol.emptyHint")}</p>
        </Alert>
      ) : (
        <div className="d-flex flex-column gap-3">
          {protocols.map((protocol) => {
            const expanded = expandedId === protocol.id;
            const percent = progressPercent(protocol);
            return (
              <div key={protocol.id} className="border rounded p-3">
                <div className="d-flex flex-wrap justify-content-between align-items-start gap-2">
                  <div className="flex-grow-1" style={{ minWidth: 200 }}>
                    <div className="d-flex align-items-center gap-2 flex-wrap">
                      <h6 className="mb-0">{protocol.name}</h6>
                      <Badge color={statusColor(protocol.status)}>
                        {t(`clients.protocol.status.${protocol.status}`)}
                      </Badge>
                      <Badge color="soft-primary" className="text-primary">
                        {t("clients.protocol.sessionOf", {
                          n: protocol.current_session_number,
                          m: protocol.total_sessions,
                        })}
                      </Badge>
                    </div>
                    <p className="text-muted small mb-2 mt-1">
                      {protocol.service?.name
                        ? `${protocol.service.name} · `
                        : ""}
                      {t("clients.protocol.doneCount", {
                        done: protocol.sessions_done,
                        total: protocol.total_sessions,
                      })}
                      {protocol.started_at
                        ? ` · ${t("clients.protocol.startedAt", {
                            date: formatDate(protocol.started_at),
                          })}`
                        : ""}
                    </p>
                    {protocol.linked_credit || protocol.credit_preview?.can_consume ? (
                      <p className="text-muted small mb-2">
                        {t("clients.protocol.credit.linked", {
                          name:
                            protocol.linked_credit?.service_package?.name ||
                            protocol.credit_preview?.label ||
                            protocol.service?.name ||
                            t("clients.protocol.credit.unnamed"),
                          remaining:
                            protocol.linked_credit?.sessions_remaining ??
                            protocol.credit_preview?.sessions_remaining ??
                            0,
                        })}
                      </p>
                    ) : null}
                    {protocol.status === "active" &&
                    (protocol.client_credit_id || protocol.service_id) &&
                    !protocol.credit_preview?.can_consume ? (
                      <Alert
                        color="warning"
                        className="d-flex flex-wrap justify-content-between align-items-center gap-2 py-2 small mb-2"
                      >
                        <span>{t("clients.protocol.credit.noBalance")}</span>
                        <Button
                          color="warning"
                          size="sm"
                          outline
                          tag={Link}
                          to={`/orders?action=new&client_id=${clientId}&item_type=package`}
                        >
                          {t("clients.protocol.credit.sellPackageCta")}
                        </Button>
                      </Alert>
                    ) : null}
                    <Progress
                      value={percent}
                      className="mb-0"
                      style={{ height: 8 }}
                      color={protocol.status === "completed" ? "primary" : "success"}
                    />
                    <p className="text-muted small mb-0 mt-1">
                      {t("clients.protocol.progressLabel", { percent })}
                    </p>
                  </div>
                  <div className="d-flex gap-2">
                    <Button
                      color="light"
                      size="sm"
                      onClick={() =>
                        setExpandedId(expanded ? null : protocol.id)
                      }
                    >
                      {expanded
                        ? t("clients.protocol.hideSessions")
                        : t("clients.protocol.showSessions")}
                    </Button>
                    {canManage && protocol.status === "active" ? (
                      <Button
                        color="soft-danger"
                        size="sm"
                        disabled={saving}
                        onClick={() => void handleCancel(protocol)}
                      >
                        {t("clients.protocol.cancel")}
                      </Button>
                    ) : null}
                  </div>
                </div>

                {expanded ? (
                  <div className="mt-3">
                    <div className="table-responsive">
                      <table className="table table-sm align-middle mb-0">
                        <thead className="table-light">
                          <tr>
                            <th>{t("clients.protocol.table.session")}</th>
                            <th>{t("clients.protocol.table.status")}</th>
                            <th>{t("clients.protocol.table.appointment")}</th>
                            <th>{t("clients.protocol.table.evolution")}</th>
                            <th className="text-end">
                              {t("clients.protocol.table.actions")}
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {(protocol.sessions ?? []).map((session) => (
                            <tr key={session.id}>
                              <td>
                                {t("clients.protocol.sessionOf", {
                                  n: session.session_number,
                                  m: protocol.total_sessions,
                                })}
                              </td>
                              <td>
                                <Badge color={statusColor(session.status)}>
                                  {t(
                                    `clients.protocol.sessionStatus.${session.status}`
                                  )}
                                </Badge>
                              </td>
                              <td>
                                {session.appointment?.start_datetime
                                  ? formatDate(session.appointment.start_datetime)
                                  : session.appointment_id
                                    ? `#${session.appointment_id}`
                                    : "—"}
                              </td>
                              <td>
                                {session.client_record_id
                                  ? `#${session.client_record_id}`
                                  : "—"}
                                {session.credit_consumed ? (
                                  <div className="small text-success">
                                    {t("clients.protocol.credit.consumedBadge")}
                                  </div>
                                ) : null}
                              </td>
                              <td className="text-end">
                                {protocol.status === "active" &&
                                session.status === "planned" ? (
                                  <div className="d-inline-flex gap-1">
                                    <Button
                                      color="success"
                                      size="sm"
                                      disabled={busySessionId === session.id}
                                      onClick={() => {
                                        setAppointmentId("");
                                        setMarkTarget({ protocol, session });
                                      }}
                                    >
                                      {t("clients.protocol.markDone")}
                                    </Button>
                                    <Button
                                      color="light"
                                      size="sm"
                                      disabled={busySessionId === session.id}
                                      onClick={() =>
                                        void handleSessionStatus(
                                          protocol,
                                          session,
                                          "skipped"
                                        )
                                      }
                                    >
                                      {t("clients.protocol.markSkipped")}
                                    </Button>
                                  </div>
                                ) : session.status !== "planned" ? (
                                  <Button
                                    color="link"
                                    size="sm"
                                    className="p-0"
                                    disabled={
                                      protocol.status !== "active" ||
                                      busySessionId === session.id
                                    }
                                    onClick={() =>
                                      void handleSessionStatus(
                                        protocol,
                                        session,
                                        "planned"
                                      )
                                    }
                                  >
                                    {t("clients.protocol.reopen")}
                                  </Button>
                                ) : null}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    {protocol.notes ? (
                      <p className="small text-muted mt-2 mb-0">
                        {protocol.notes}
                      </p>
                    ) : null}
                    <ClinicalProtocolChecklistPanel
                      protocol={protocol}
                      canEdit={canView && protocol.status !== "cancelled"}
                    />
                    <ClinicalProtocolMapPanel
                      protocol={protocol}
                      canEdit={canView && protocol.status !== "cancelled"}
                    />
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      )}

      <Modal
        isOpen={markTarget !== null}
        toggle={() => {
          setMarkTarget(null);
          setAppointmentId("");
        }}
        centered
      >
        <ModalHeader
          toggle={() => {
            setMarkTarget(null);
            setAppointmentId("");
            setConsumeCredit(true);
          }}
        >
          {t("clients.protocol.markModal.title")}
        </ModalHeader>
        <ModalBody>
          {markTarget ? (
            <p className="mb-3">
              {t("clients.protocol.markModal.subtitle", {
                n: markTarget.session.session_number,
                m: markTarget.protocol.total_sessions,
                name: markTarget.protocol.name,
              })}
            </p>
          ) : null}
          {markTarget?.protocol.credit_preview?.can_consume ? (
            <FormGroup check className="mb-3">
              <Input
                id="protocol-mark-consume-credit"
                type="checkbox"
                checked={consumeCredit}
                onChange={(e) => setConsumeCredit(e.target.checked)}
              />
              <Label check for="protocol-mark-consume-credit">
                {t("clients.protocol.markModal.consumeCredit", {
                  name:
                    markTarget.protocol.credit_preview.label ||
                    t("clients.protocol.credit.unnamed"),
                  remaining: markTarget.protocol.credit_preview.sessions_remaining,
                })}
              </Label>
            </FormGroup>
          ) : markTarget?.protocol.client_credit_id ||
            markTarget?.protocol.service_id ? (
            <Alert
              color="warning"
              className="d-flex flex-wrap justify-content-between align-items-center gap-2 small py-2"
            >
              <span>{t("clients.protocol.credit.noBalance")}</span>
              <Button
                color="warning"
                size="sm"
                outline
                tag={Link}
                to={`/orders?action=new&client_id=${clientId}&item_type=package`}
              >
                {t("clients.protocol.credit.sellPackageCta")}
              </Button>
            </Alert>
          ) : null}
          <FormGroup className="mb-0">
            <Label for="protocol-mark-appointment">
              {t("clients.protocol.markModal.appointmentOptional")}
            </Label>
            {loadingAppointments ? (
              <div className="py-2">
                <Spinner size="sm" color="primary" />
              </div>
            ) : (
              <Input
                id="protocol-mark-appointment"
                type="select"
                value={appointmentId}
                onChange={(e) => setAppointmentId(e.target.value)}
              >
                <option value="">
                  {t("clients.protocol.markModal.noAppointment")}
                </option>
                {appointments.map((appointment) => (
                  <option key={appointment.id} value={appointment.id}>
                    {formatDate(appointment.start_datetime)}
                    {appointment.service?.name
                      ? ` · ${appointment.service.name}`
                      : ""}
                  </option>
                ))}
              </Input>
            )}
            <p className="text-muted small mt-2 mb-0">
              {t("clients.protocol.markModal.hint")}
            </p>
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <Button
            color="light"
            onClick={() => {
              setMarkTarget(null);
              setAppointmentId("");
              setConsumeCredit(true);
            }}
          >
            {t("clients.protocol.markModal.cancel")}
          </Button>
          <Button
            color="success"
            disabled={busySessionId !== null}
            onClick={() => void confirmMarkDone()}
          >
            {t("clients.protocol.markModal.confirm")}
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default ClientClinicalProtocolsPanel;
